import React from 'react';
import { X, AlertTriangle, Check, Trash2 } from 'lucide-react';
import { TrayEdge, TrayNode } from '../../store/editorStore';
import { FIELD_STYLE, ROW, LABEL, SECTION } from './PropertiesEditorStyles';
import { pathwayEdgeName } from '../../utils/naming';
import {
  getTrayMediaConfig,
  calculateTrayMaxCables,
  calculateTrayFillStatus,
  checkProximitySegregation,
  TrayMedia,
  TrayConstruction,
} from '../../utils/traySegregation.logic';

interface Props {
  edge: TrayEdge;
  sourceNode?: TrayNode;
  targetNode?: TrayNode;
  allEdges?: Record<string, TrayEdge>;
  allNodes?: Record<string, TrayNode>;
  cableCount: number;
  onUpdate: (id: string, updates: Partial<TrayEdge>) => void;
  onUpdateNode: (node: TrayNode) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
  impactPreview?: { affected_routes?: Array<{ cable_id: string; edge_ids: string[] }> } | null;
  onPreview?: (updates: Partial<TrayEdge>) => void;
  capacityAudit?: { overloaded?: boolean; used?: number; limit?: number | null; unknown?: boolean } | null;
}

const WIDTH_OPTIONS = [
  { label: '100 мм', value: 0.1 },
  { label: '200 мм', value: 0.2 },
  { label: '300 мм', value: 0.3 },
  { label: '400 мм', value: 0.4 },
  { label: '600 мм', value: 0.6 },
];

const CONSTRUCTION_OPTIONS: { label: string; value: TrayConstruction }[] = [
  { label: 'Оптический короб (Fiber Raceway)', value: 'fiber_raceway' },
  { label: 'Проволочный лоток (Wire Mesh)', value: 'wire_mesh' },
  { label: 'Шинный мост (Power Busway)', value: 'power_busway' },
  { label: 'Лестничный кабель-рост (Ladder)', value: 'ladder' },
  { label: 'Под фальшполом (Underfloor)', value: 'underfloor' },
];

export const TrayInspector: React.FC<Props> = ({
  edge,
  sourceNode,
  targetNode,
  allEdges,
  allNodes,
  cableCount,
  onUpdate,
  onUpdateNode,
  onDelete,
  onClose,
  impactPreview,
  onPreview,
  capacityAudit,
}) => {
  const currentMedia = (edge.mediaType || edge.type || 'copper') as TrayMedia;
  const config = getTrayMediaConfig(currentMedia);
  const widthM = edge.widthM || 0.3;
  const capacityEnabled = edge.fillCalcEnabled === true;
  const maxCables = edge.maxCables || calculateTrayMaxCables(widthM, currentMedia);
  const fillStatus = calculateTrayFillStatus(cableCount, maxCables);

  // Length in meters (PPM = 100 px/m)
  const lengthM =
    sourceNode && targetNode
      ? +(
          Math.sqrt(
            (targetNode.x - sourceNode.x) ** 2 + (targetNode.y - sourceNode.y) ** 2
          ) / 100
        ).toFixed(2)
      : 0;

  // TIA-942 proximity clearance check against other tray segments
  const proximityViolations: string[] = [];
  if (sourceNode && targetNode && allEdges && allNodes) {
    const currentSegment = {
      id: edge.id,
      type: currentMedia,
      sourcePos: { x: sourceNode.x, y: sourceNode.y },
      targetPos: { x: targetNode.x, y: targetNode.y },
    };
    Object.values(allEdges).forEach(otherEdge => {
      if (otherEdge.id === edge.id) return;
      const s = allNodes[otherEdge.source];
      const t = allNodes[otherEdge.target];
      if (!s || !t) return;
      if (
        otherEdge.source === edge.source ||
        otherEdge.source === edge.target ||
        otherEdge.target === edge.source ||
        otherEdge.target === edge.target
      ) {
        return;
      }
      const otherSegment = {
        id: otherEdge.id,
        type: otherEdge.mediaType || otherEdge.type || 'copper',
        sourcePos: { x: s.x, y: s.y },
        targetPos: { x: t.x, y: t.y },
      };
      const check = checkProximitySegregation(currentSegment, otherSegment, 0.3);
      if (check.hasViolation && check.message) {
        proximityViolations.push(check.message);
      }
    });
  }

  const handleMediaChange = (media: TrayMedia) => {
    const nextConfig = getTrayMediaConfig(media);
    onUpdate(edge.id, {
      type: media,
      mediaType: media,
      trayType: nextConfig.defaultType,
      color: nextConfig.color,
    });
  };

  const handleStatusChange = (status: NonNullable<TrayEdge['status']>) => {
    onPreview?.({ status });
    onUpdate(edge.id, { status });
  };

  const handleWidthChange = (newWidthM: number) => {
    onUpdate(edge.id, {
      widthM: newWidthM,
    });
  };

  const enableCapacityAudit = () => {
    onPreview?.({ fillCalcEnabled: true, capacityProfile: 'count' });
    onUpdate(edge.id, {
      fillCalcEnabled: true,
      capacityProfile: edge.capacityProfile || 'count',
      capacityEnforcement: false,
      maxCables: edge.maxCables || calculateTrayMaxCables(widthM, currentMedia),
    });
  };

  return (
    <div style={{ padding: '12px', fontSize: 12 }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <div style={SECTION}>Кабельная трасса</div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="Снять выделение"
        >
          <X size={13} />
        </button>
      </div>

      <div style={{ marginBottom: 14, overflowWrap: 'anywhere', color: 'var(--text-secondary)' }}>
        {pathwayEdgeName(edge, Object.values(allNodes || {}))}
        <div style={{ ...LABEL, fontFamily: 'var(--font-data)', marginTop: 4 }}>ID {edge.id}</div>
      </div>
      <div style={ROW}>
        <label style={LABEL} htmlFor="pathway-segment-name">Название сегмента</label>
        <input id="pathway-segment-name" style={{ ...FIELD_STYLE, minHeight: 36, outline: undefined }}
          value={typeof edge.name === 'string' ? edge.name : edge.name === undefined && typeof edge.label === 'string' ? edge.label : ''} onChange={event => onUpdate(edge.id, { name: event.target.value.trim() ? event.target.value : null })} />
      </div>
      {([['Начальный узел', sourceNode], ['Конечный узел', targetNode]] as const).map(([label, node], index) => <div key={index} style={ROW}>
        <label style={LABEL} htmlFor={'pathway-endpoint-name-' + index}>{label === 'Начальный узел' ? 'Название начального узла' : 'Название конечного узла'}</label>
        <input id={'pathway-endpoint-name-' + index} style={{ ...FIELD_STYLE, minHeight: 36, outline: undefined }} disabled={!node}
          value={typeof node?.name === 'string' ? node.name : ''} onChange={event => { if (node) onUpdateNode({ ...node, name: event.target.value.trim() ? event.target.value : null }); }} />
        <span style={{ ...LABEL, overflowWrap: 'anywhere', fontFamily: 'var(--font-data)' }}>{node ? 'ID ' + node.id : 'Узел отсутствует'}</span>
      </div>)}

      {/* Media Type Selector */}
      <div style={ROW}>
        <span style={LABEL}>Среда передачи (TIA-942)</span>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
          <button
            onClick={() => handleMediaChange('fiber')}
            style={{
              padding: '6px 4px',
              borderRadius: 4,
              border: `1px solid ${currentMedia === 'fiber' ? '#f59e0b' : '#374151'}`,
              background: currentMedia === 'fiber' ? 'rgba(245, 158, 11, 0.2)' : '#111827',
              color: currentMedia === 'fiber' ? '#fbbf24' : '#9ca3af',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: currentMedia === 'fiber' ? 600 : 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
            Оптика
          </button>
          <button
            onClick={() => handleMediaChange('copper')}
            style={{
              padding: '6px 4px',
              borderRadius: 4,
              border: `1px solid ${currentMedia === 'copper' ? '#0ea5e9' : '#374151'}`,
              background: currentMedia === 'copper' ? 'rgba(14, 165, 233, 0.2)' : '#111827',
              color: currentMedia === 'copper' ? '#38bdf8' : '#9ca3af',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: currentMedia === 'copper' ? 600 : 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#0ea5e9', flexShrink: 0 }} />
            Медь
          </button>
          <button
            onClick={() => handleMediaChange('power')}
            style={{
              padding: '6px 4px',
              borderRadius: 4,
              border: `1px solid ${currentMedia === 'power' ? '#ef4444' : '#374151'}`,
              background: currentMedia === 'power' ? 'rgba(239, 68, 68, 0.2)' : '#111827',
              color: currentMedia === 'power' ? '#f87171' : '#9ca3af',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: currentMedia === 'power' ? 600 : 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
            Сила
          </button>
        </div>
      </div>

      {/* Construction type */}
      <div style={ROW}>
        <span style={LABEL}>Конструкция лотка</span>
        <select
          value={edge.trayType || config.defaultType}
          onChange={e => onUpdate(edge.id, { trayType: e.target.value as TrayConstruction })}
          style={FIELD_STYLE}
        >
          {CONSTRUCTION_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Width selector */}
      <div style={ROW}>
        <span style={LABEL}>Состояние Pathway</span>
        <select value={edge.status || 'active'} onChange={e => handleStatusChange(e.target.value as NonNullable<TrayEdge['status']>)} style={FIELD_STYLE}>
          <option value="planned">Запланирован</option>
          <option value="active">Активен</option>
          <option value="restricted">Ограничен</option>
          <option value="out_of_service">Выведен из эксплуатации</option>
          <option value="removed">Удалён</option>
        </select>
      </div>
      {impactPreview && (impactPreview.affected_routes?.length || 0) > 0 && (
        <div style={{ ...ROW, borderLeft: '3px solid #f59e0b', paddingLeft: 8, color: '#fbbf24' }}>
          Изменение затрагивает занятые маршруты: {impactPreview.affected_routes?.length}. Публикация будет заблокирована до переноса кабелей.
        </div>
      )}

      {/* Width selector */}
      <div style={ROW}>
        <span style={LABEL}>Ширина лотка</span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 3 }}>
          {WIDTH_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => handleWidthChange(opt.value)}
              style={{
                padding: '4px 2px',
                borderRadius: 4,
                border: `1px solid ${Math.abs(widthM - opt.value) < 0.01 ? '#3b82f6' : '#374151'}`,
                background: Math.abs(widthM - opt.value) < 0.01 ? 'rgba(59, 130, 246, 0.2)' : '#111827',
                color: Math.abs(widthM - opt.value) < 0.01 ? '#60a5fa' : '#9ca3af',
                cursor: 'pointer',
                fontSize: 10,
                fontWeight: Math.abs(widthM - opt.value) < 0.01 ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Geometry and Length */}
      <div style={{ ...ROW, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px' }}>
        <div>
          <span style={LABEL}>Длина сегмента</span>
          <div style={{ color: '#f3f4f6', fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
            {lengthM} м
          </div>
        </div>
        <div>
          <span style={LABEL}>Сечение</span>
          <div style={{ color: '#f3f4f6', fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
            {Math.round(widthM * 1000)} мм
          </div>
        </div>
      </div>

      {/* Capacity audit is opt-in; enabling it never blocks existing cables. */}
      <div
        style={{
          background: 'rgba(17, 24, 39, 0.7)',
          border: '1px solid #1f2937',
          borderRadius: 6,
          padding: '10px',
          marginBottom: 12,
        }}
      >
        {!capacityEnabled ? (
          <>
            <div style={{ fontWeight: 600, color: '#cbd5e1', marginBottom: 5 }}>Контроль вместимости выключен</div>
            <div style={{ color: '#94a3b8', fontSize: 10, lineHeight: 1.4, marginBottom: 8 }}>
              Маршруты не блокируются. Включите аудит, когда данные по трассе готовы.
            </div>
            <button onClick={enableCapacityAudit} style={{ ...FIELD_STYLE, color: '#cbd5e1', cursor: 'pointer' }}>
              Включить аудит
            </button>
          </>
        ) : <>
        {capacityAudit?.overloaded && (
          <div style={{ marginBottom: 8, color: '#fca5a5', fontSize: 10 }}>
            Аудит: вместимость превышена. Существующие кабели сохранены, новые маршруты пока не блокируются.
          </div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 6,
          }}
        >
          <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>
            Заполнение (TIA-569)
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: '1px 6px',
              borderRadius: 3,
              background: `${fillStatus.color}22`,
              color: fillStatus.color,
              border: `1px solid ${fillStatus.color}55`,
            }}
          >
            {fillStatus.percent}%
          </span>
        </div>

        {/* Progress bar */}
        <div
          style={{
            height: 6,
            background: '#1f2937',
            borderRadius: 3,
            overflow: 'hidden',
            marginBottom: 8,
          }}
        >
          <div
            style={{
              width: `${Math.min(100, fillStatus.percent)}%`,
              height: '100%',
              background: fillStatus.color,
              borderRadius: 3,
              transition: 'width 0.2s',
            }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8' }}>
          <span>Проложено: <strong style={{ color: '#f3f4f6' }}>{cableCount} шт.</strong></span>
          <span>Профиль: <strong style={{ color: '#f3f4f6' }}>{edge.capacityProfile === 'fill_ratio' ? 'fill-ratio' : 'count'}</strong></span>
        </div>
        <select
          value={edge.capacityProfile || 'count'}
          onChange={event => onUpdate(edge.id, { capacityProfile: event.target.value as 'count' | 'fill_ratio' })}
          style={{ ...FIELD_STYLE, marginTop: 8 }}
        >
          <option value="count">Количество кабелей</option>
          <option value="fill_ratio">Заполнение сечения</option>
        </select>
        {edge.capacityProfile !== 'fill_ratio' ? (
          <label style={{ display: 'block', marginTop: 6, color: '#94a3b8', fontSize: 10 }}>
            Максимум кабелей
            <input
              type="number"
              min={0}
              value={edge.maxCables ?? maxCables}
              onChange={event => onUpdate(edge.id, { maxCables: Math.max(0, Number(event.target.value)) })}
              style={{ ...FIELD_STYLE, marginTop: 3 }}
            />
          </label>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
            <label style={{ color: '#94a3b8', fontSize: 10 }}>
              Сечение, мм²
              <input
                type="number"
                min={1}
                value={edge.capacityAreaMm2 ?? ''}
                placeholder="например 5000"
                onChange={event => onUpdate(edge.id, { capacityAreaMm2: Number(event.target.value) || undefined })}
                style={{ ...FIELD_STYLE, marginTop: 3 }}
              />
            </label>
            <label style={{ color: '#94a3b8', fontSize: 10 }}>
              Доля заполнения
              <input
                type="number"
                min={0.01}
                max={1}
                step={0.01}
                value={edge.fillRatio ?? 0.4}
                onChange={event => onUpdate(edge.id, { fillRatio: Math.min(1, Math.max(0.01, Number(event.target.value))) })}
                style={{ ...FIELD_STYLE, marginTop: 3 }}
              />
            </label>
          </div>
        )}
        <div style={{ marginTop: 6, fontSize: 10, color: '#94a3b8' }}>
          Первый аудит только сообщает о превышении. Блокировку новых маршрутов включайте отдельно после проверки.
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, color: '#cbd5e1', fontSize: 10 }}>
          <input
            type="checkbox"
            checked={edge.capacityEnforcement === true}
            onChange={event => onUpdate(edge.id, { capacityEnforcement: event.target.checked })}
          />
          Блокировать новые перегружающие маршруты
        </label>
        </>}
      </div>

      {/* TIA-942 Proximity Clearance Validation */}
      <div
        style={{
          borderLeft: `3px solid ${proximityViolations.length > 0 ? '#ef4444' : '#22c55e'}`,
          padding: '8px 10px',
          background: proximityViolations.length > 0 ? 'rgba(239, 68, 68, 0.12)' : 'rgba(34, 197, 94, 0.1)',
          borderRadius: '0 4px 4px 0',
          fontSize: 11,
          color: proximityViolations.length > 0 ? '#fca5a5' : '#86efac',
          marginBottom: 12,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
          {proximityViolations.length > 0 ? (
            <>
              <AlertTriangle size={13} style={{ flexShrink: 0 }} />
              <span>Нарушение TIA-942 (ЭМП-риск)</span>
            </>
          ) : (
            <>
              <Check size={13} style={{ flexShrink: 0 }} />
              <span>TIA-942: Разделение сред соблюдено</span>
            </>
          )}
        </div>
        <div style={{ fontSize: 10, color: '#9ca3af' }}>
          {proximityViolations.length > 0
            ? proximityViolations[0]
            : 'Отступ между силовой и информационной трассой ≥ 300 мм'}
        </div>
      </div>

      {/* Segregation rule note */}
      <div
        style={{
          borderLeft: `3px solid ${config.color}`,
          padding: '6px 8px',
          background: 'rgba(0,0,0,0.2)',
          borderRadius: '0 4px 4px 0',
          fontSize: 10,
          color: '#9ca3af',
          marginBottom: 14,
        }}
      >
        {currentMedia === 'fiber' && 'Оптическая трасса: защита радиуса изгиба, изоляция от медных жгутов.'}
        {currentMedia === 'copper' && 'Информационная трасса: требование отступа >= 300 мм от силовых шин.'}
        {currentMedia === 'power' && 'Силовая трасса: электромагнитный барьер, заземление металлоконструкций.'}
      </div>

      {/* Delete button */}
      <button
        disabled={cableCount > 0}
        onClick={() => onDelete(edge.id)}
        style={{
          width: '100%',
          padding: '7px',
          background: 'rgba(239, 68, 68, 0.12)',
          border: '1px solid rgba(239, 68, 68, 0.35)',
          color: '#ef4444',
          borderRadius: 5,
          cursor: cableCount > 0 ? 'not-allowed' : 'pointer',
          opacity: cableCount > 0 ? 0.5 : 1,
          fontSize: 11,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        <Trash2 size={13} /> {cableCount > 0 ? 'Удаление заблокировано: есть кабели' : 'Удалить трассу'}
      </button>
    </div>
  );
};
